import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'context/AuthContext';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import { aujourdHui } from '../../../utils/jour';

const EQUIPEMENTS = [
  { code: 'radio', label: 'Radio' },
  { code: 'lampe', label: 'Lampe torche' },
  { code: 'badge', label: 'Badge' },
  { code: 'tenue', label: 'Tenue complete' },
  { code: 'gilet', label: 'Gilet' },
  { code: 'carnet', label: 'Carnet de service' },
];

const TABS = [
  { id: 'prises', label: 'Prises de service' },
  { id: 'rondes', label: 'Rondes' },
  { id: 'points', label: 'Points de passage' },
  { id: 'alertes', label: 'Alertes' },
];

export default function Rondes() {
  const { token } = useAuth();
  const [tab, setTab] = useState('prises');
  const [stats, setStats] = useState({});
  const [rondeStats, setRondeStats] = useState({});

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadStats = useCallback(async () => {
    try {
      const [psRes, rdRes] = await Promise.all([
        api.get('/prises-service/stats'),
        api.get('/rondes/stats'),
      ]);
      setStats(psRes.data);
      setRondeStats(rdRes.data);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="page-container">
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Prises de service</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg></div></div>
          <div className="stat-value">{stats.prises_aujourd_hui || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Vacations prevues</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div></div>
          <div className="stat-value">{stats.vacations_prevues || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Agents en retard</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-value" style={{ color: (stats.agents_en_retard || 0) > 0 ? '#EF4444' : undefined }}>
            {stats.agents_en_retard || 0}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Rondes terminees</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></div></div>
          <div className="stat-value">{rondeStats.complete || 0}/{rondeStats.total || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Alertes actives</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></div></div>
          <div className="stat-value" style={{ color: (rondeStats.alertes_actives || 0) > 0 ? '#EF4444' : undefined }}>
            {rondeStats.alertes_actives || 0}
          </div>
        </div>
      </div>

      <div className="tab-bar">
        {TABS.map(t => (
          <button key={t.id} className={`tab-btn ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'prises' && <TabPrisesService headers={headers} onUpdate={loadStats} />}
      {tab === 'rondes' && <TabRondes headers={headers} onUpdate={loadStats} />}
      {tab === 'points' && <TabPoints headers={headers} />}
      {tab === 'alertes' && <TabAlertes headers={headers} />}
    </div>
  );
}

// ─── PRISES DE SERVICE ──────────────────────────────────────────
function TabPrisesService({ headers, onUpdate }) {
  const [prises, setPrises] = useState([]);
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ agent_id: '', site_id: '', equipements_recus: [], commentaire: '' });
  const [badgeMode, setBadgeMode] = useState(false);
  const [badgeCode, setBadgeCode] = useState('');
  const [badgeResult, setBadgeResult] = useState(null);
  const [dateFiltre, setDateFiltre] = useState(aujourdHui());
  const [loading, setLoading] = useState(false);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      const [psRes, sitesRes, agentsRes] = await Promise.all([
        api.get('/prises-service', { params: { date: dateFiltre } }),
        api.get('/sites').catch(() => null),
        api.get('/agents').catch(() => null),
      ]);
      setPrises(psRes.data);
      if (sitesRes) setSites(sitesRes.data);
      if (agentsRes) setAgents(agentsRes.data);
    } catch {}
  }, [dateFiltre]);

  useEffect(() => { load(); }, [load]);

  const handleBadgeScan = async () => {
    if (!badgeCode.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/prises-service/authentifier-badge', { badge_code: badgeCode });
      const data = res.data;
      setBadgeResult(data);
      if (data.vacations?.length > 0) {
        const vac = data.vacations[0];
        setForm(f => ({
          ...f,
          agent_id: data.agent.id,
          site_id: vac.site_id || '',
        }));
      } else {
        setForm(f => ({ ...f, agent_id: data.agent.id }));
      }
      setShowForm(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Badge non reconnu');
    }
    setLoading(false);
  };

  const toggleEquipement = (code) => {
    setForm(f => ({
      ...f,
      equipements_recus: f.equipements_recus.includes(code)
        ? f.equipements_recus.filter(e => e !== code)
        : [...f.equipements_recus, code],
    }));
  };

  const submit = async () => {
    if (!form.agent_id || !form.site_id) return alert('Agent et site requis');
    setLoading(true);
    try {
      await api.post('/prises-service', form);
      setShowForm(false);
      setBadgeMode(false);
      setBadgeResult(null);
      setBadgeCode('');
      setForm({ agent_id: '', site_id: '', equipements_recus: [], commentaire: '' });
      load();
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const selectedSite = badgeResult?.vacations?.find(v => v.site_id === form.site_id);

  return (
    <div>
      <div className="section-toolbar">
        <input type="date" value={dateFiltre} onChange={e => setDateFiltre(e.target.value)} className="gss-input" />
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="gss-btn secondary" onClick={() => { setBadgeMode(true); setShowForm(false); }}>
            Scanner badge QR
          </button>
          <button className="gss-btn primary" onClick={() => { setShowForm(true); setBadgeMode(false); }}>
            + Prise manuelle
          </button>
        </div>
      </div>

      {badgeMode && (
        <div className="gss-modal-overlay" onClick={() => { setBadgeMode(false); setBadgeResult(null); setBadgeCode(''); }}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                <div>
                  Scanner le badge QR
                  <div className="gss-modal-subtitle">Authentification par badge QR</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => { setBadgeMode(false); setBadgeResult(null); setBadgeCode(''); }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="gss-input"
                  placeholder="Code badge (scannez ou saisissez)"
                  value={badgeCode}
                  onChange={e => setBadgeCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleBadgeScan()}
                  autoFocus
                  style={{ flex: 1 }}
                />
                <button className="gss-btn gss-btn-primary" onClick={handleBadgeScan} disabled={loading}>
                  {loading ? 'Verification...' : 'Verifier'}
                </button>
              </div>
              {badgeResult && (
                <div style={{ marginTop: 12, padding: 12, background: '#F0FDF4', borderRadius: 8 }}>
                  <strong>{badgeResult.agent.prenom} {badgeResult.agent.nom}</strong> — {badgeResult.agent.matricule}
                  {badgeResult.deja_prise_service && (
                    <div style={{ color: '#D97706', marginTop: 4 }}>
                      Deja pris son service aujourd'hui a {new Date(badgeResult.deja_prise_service.heure_prise).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {badgeResult.vacations?.length > 0 && (
                    <div style={{ marginTop: 4, color: '#059669' }}>
                      Vacation prevue : {badgeResult.vacations[0].site_nom} ({badgeResult.vacations[0].heure_debut} - {badgeResult.vacations[0].heure_fin})
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => { setShowForm(false); setBadgeMode(false); setBadgeResult(null); }}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
                <div>
                  Confirmer la prise de service
                  <div className="gss-modal-subtitle">Confirmation de prise de poste</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => { setShowForm(false); setBadgeMode(false); setBadgeResult(null); }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <span>IDENTIFICATION</span>
              </div>
              <div className="gss-form-grid">
                {!badgeResult && (
                  <>
                    <div className="gss-field">
                      <label>Agent</label>
                      <select className="gss-input" value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}>
                        <option value="">-- Selectionner --</option>
                        {agents.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom} ({a.matricule})</option>)}
                      </select>
                    </div>
                    <div className="gss-field">
                      <label>Site</label>
                      <select className="gss-input" value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
                        <option value="">-- Selectionner --</option>
                        {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                      </select>
                    </div>
                  </>
                )}
                {badgeResult && (
                  <div className="gss-field gss-field-full">
                    <div style={{ padding: 12, background: '#EFF6FF', borderRadius: 8 }}>
                      <strong>Agent :</strong> {badgeResult.agent.prenom} {badgeResult.agent.nom}<br />
                      {selectedSite && (
                        <>
                          <strong>Site :</strong> {selectedSite.site_nom}<br />
                          <strong>Adresse :</strong> {selectedSite.site_adresse}<br />
                          {selectedSite.site_instructions && (
                            <>
                              <strong>Consignes :</strong>
                              <div style={{ marginTop: 4, padding: 8, background: '#FEF3C7', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                                {selectedSite.site_instructions}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <span>EQUIPEMENTS & COMMENTAIRE</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field gss-field-full">
                  <label>Equipements recus</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {EQUIPEMENTS.map(eq => (
                      <label key={eq.code} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', borderRadius: 6,
                        background: form.equipements_recus.includes(eq.code) ? '#DCFCE7' : '#F3F4F6',
                        border: `1px solid ${form.equipements_recus.includes(eq.code) ? '#22C55E' : '#D1D5DB'}`,
                        cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          checked={form.equipements_recus.includes(eq.code)}
                          onChange={() => toggleEquipement(eq.code)}
                        />
                        {eq.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="gss-field gss-field-full">
                  <label>Commentaire</label>
                  <textarea className="gss-input" rows={2} value={form.commentaire}
                    onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => { setShowForm(false); setBadgeMode(false); setBadgeResult(null); }}>Annuler</button>
              <button className="gss-btn-deploy" onClick={submit} disabled={loading}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                {loading ? 'Enregistrement...' : 'Confirmer prise de poste'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="gss-table-wrapper">
        <table className="gss-table">
          <thead>
            <tr>
              <th>Heure</th>
              <th>Agent</th>
              <th>Matricule</th>
              <th>Site</th>
              <th>Methode</th>
              <th>Equipements</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {prises.length === 0 && (
              <tr><td colSpan="7" style={{ padding: 0, border: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucune ronde enregistree</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
                </div>
              </td></tr>
            )}
            {prises.map(p => {
              const eqs = Array.isArray(p.equipements_recus) ? p.equipements_recus : [];
              return (
                <tr key={p.id}>
                  <td>{new Date(p.heure_prise).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td>{p.agent_prenom} {p.agent_nom}</td>
                  <td><span className="mono">{p.matricule}</span></td>
                  <td>{p.site_nom}</td>
                  <td>
                    <span className={`gss-badge ${p.methode_auth === 'qr_code' ? 'success' : 'info'}`}>
                      {p.methode_auth === 'qr_code' ? 'QR Badge' : 'Manuel'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12 }}>
                      {eqs.length > 0 ? eqs.map(e => EQUIPEMENTS.find(eq => eq.code === e)?.label || e).join(', ') : '-'}
                    </span>
                  </td>
                  <td><span className="gss-badge success">{p.statut}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── RONDES ──────────────────────────────────────────
function TabRondes({ headers, onUpdate }) {
  const [rondes, setRondes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ agent_id: '', site_id: '' });
  const [dateFiltre, setDateFiltre] = useState(aujourdHui());
  const [siteFiltre, setSiteFiltre] = useState('');
  const [agentFiltre, setAgentFiltre] = useState('');
  const [statutFiltre, setStatutFiltre] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { date: dateFiltre };
      if (siteFiltre) params.site_id = siteFiltre;
      if (agentFiltre) params.agent_id = agentFiltre;
      if (statutFiltre) params.statut = statutFiltre;
      const [rdRes, sitesRes, agentsRes] = await Promise.all([
        api.get('/rondes', { params }),
        api.get('/sites').catch(() => null),
        api.get('/agents').catch(() => null),
      ]);
      setRondes(rdRes.data);
      if (sitesRes) setSites(sitesRes.data);
      if (agentsRes) setAgents(agentsRes.data);
    } catch {}
  }, [dateFiltre, siteFiltre, agentFiltre, statutFiltre]);

  useEffect(() => { load(); }, [load]);

  const demarrer = async () => {
    if (!form.agent_id || !form.site_id) return alert('Agent et site requis');
    setLoading(true);
    try {
      await api.post('/rondes', form);
      setShowForm(false);
      setForm({ agent_id: '', site_id: '' });
      load();
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const voirDetail = async (id) => {
    try {
      const res = await api.get(`/rondes/${id}`);
      setDetail(res.data);
    } catch {}
  };

  const terminer = async (id) => {
    try {
      await api.put(`/rondes/${id}/terminer`, {});
      load(); onUpdate(); setDetail(null);
    } catch {}
  };

  // Compute local stats from loaded rondes
  const localStats = {
    total: rondes.length,
    complete: rondes.filter(r => r.statut === 'complete').length,
    en_cours: rondes.filter(r => r.statut === 'en_cours').length,
    incomplete: rondes.filter(r => r.statut === 'incomplete').length,
    alertes: rondes.filter(r => {
      if (r.statut !== 'complete' && r.statut !== 'incomplete') return false;
      return r.nb_points_scannes < r.nb_points_total;
    }).length,
  };

  const statutColor = (s) => {
    if (s === 'complete') return 'success';
    if (s === 'en_cours') return 'warning';
    return 'danger';
  };

  return (
    <div>
      {/* Stats resume rondes */}
      <div className="stats-cards" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total rondes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-value">{localStats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Completes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-value" style={{ color: '#22C55E' }}>{localStats.complete}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">En cours</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4"/><path d="M12 18v4"/><path d="M4.93 4.93l2.83 2.83"/><path d="M16.24 16.24l2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="M4.93 19.07l2.83-2.83"/><path d="M16.24 7.76l2.83-2.83"/></svg></div></div>
          <div className="stat-value" style={{ color: '#F59E0B' }}>{localStats.en_cours}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Incompletes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg></div></div>
          <div className="stat-value" style={{ color: '#6B7280' }}>{localStats.incomplete}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Points manques</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div></div>
          <div className="stat-value" style={{ color: localStats.alertes > 0 ? '#EF4444' : undefined }}>{localStats.alertes}</div>
        </div>
      </div>

      <div className="section-toolbar">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="date" value={dateFiltre} onChange={e => setDateFiltre(e.target.value)} className="gss-input" />
          <select className="gss-input" value={siteFiltre} onChange={e => setSiteFiltre(e.target.value)} style={{ maxWidth: 180 }}>
            <option value="">Tous sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <select className="gss-input" value={agentFiltre} onChange={e => setAgentFiltre(e.target.value)} style={{ maxWidth: 180 }}>
            <option value="">Tous agents</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
          </select>
          <select className="gss-input" value={statutFiltre} onChange={e => setStatutFiltre(e.target.value)} style={{ maxWidth: 140 }}>
            <option value="">Tous statuts</option>
            <option value="en_cours">En cours</option>
            <option value="complete">Complete</option>
            <option value="incomplete">Incomplete</option>
          </select>
        </div>
        <button className="gss-btn primary" onClick={() => setShowForm(true)}>+ Demarrer ronde</button>
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <div>
                  Nouvelle ronde
                  <div className="gss-modal-subtitle">Demarrage d'une ronde de surveillance</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <span>AFFECTATION</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Agent</label>
                  <select className="gss-input" value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Site</label>
                  <select className="gss-input" value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={demarrer} disabled={loading}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
                Demarrer
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="gss-modal-overlay" onClick={() => setDetail(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Detail ronde — {detail.site_nom}</h3>
              <button className="gss-btn secondary" onClick={() => setDetail(null)}>Fermer</button>
            </div>
            <div style={{ marginTop: 8 }}>
              <strong>Agent :</strong> {detail.agent_prenom} {detail.agent_nom} ({detail.matricule})<br />
              <strong>Debut :</strong> {new Date(detail.debut).toLocaleString('fr-FR')}<br />
              {detail.fin && <><strong>Fin :</strong> {new Date(detail.fin).toLocaleString('fr-FR')}<br /></>}
              <strong>Statut :</strong> <span className={`gss-badge ${statutColor(detail.statut)}`}>{detail.statut}</span><br />
              <strong>Progression :</strong> {detail.nb_points_scannes}/{detail.nb_points_total} points
              <div style={{ marginTop: 8, height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: detail.nb_points_total > 0 ? `${(detail.nb_points_scannes / detail.nb_points_total) * 100}%` : '0%',
                  height: '100%',
                  background: detail.nb_points_scannes >= detail.nb_points_total ? '#22C55E' : '#F59E0B',
                  borderRadius: 4, transition: 'width 0.3s',
                }} />
              </div>
            </div>

            {/* Alerte points obligatoires manques */}
            {detail.points?.some(p => p.obligatoire && !p.scanne) && (
              <div style={{ marginTop: 12, padding: 10, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#DC2626', fontWeight: 600, fontSize: 13 }}>
                ATTENTION : Des points obligatoires n'ont pas ete scannes !
              </div>
            )}

            <h4 style={{ marginTop: 16 }}>Points de passage</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, maxHeight: 320, overflowY: 'auto' }}>
              {detail.points?.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                  borderRadius: 6, background: p.scanne ? '#F0FDF4' : '#FEF2F2',
                  border: `1px solid ${p.scanne ? '#BBF7D0' : '#FECACA'}`,
                }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: p.scanne ? '#22C55E' : '#EF4444', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                    {p.scanne ? '\u2713' : i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong>{p.nom}</strong>
                    {p.obligatoire && <span style={{ color: '#EF4444', fontSize: 11, marginLeft: 6 }}>OBLIGATOIRE</span>}
                    {p.description && <div style={{ fontSize: 12, color: '#6B7280' }}>{p.description}</div>}
                  </div>
                  {p.scan && (
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                      {new Date(p.scan.scanned_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {detail.statut === 'en_cours' && (
              <button className="gss-btn warning" style={{ marginTop: 12 }} onClick={() => terminer(detail.id)}>
                Terminer la ronde
              </button>
            )}
          </div>
        </div>
      )}

      <div className="gss-table-wrapper">
        <table className="gss-table">
          <thead>
            <tr>
              <th>Debut</th>
              <th>Fin</th>
              <th>Agent</th>
              <th>Site</th>
              <th>Progression</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rondes.length === 0 && (
              <tr><td colSpan="7" style={{ padding: 0, border: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucune ronde enregistree</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
                </div>
              </td></tr>
            )}
            {rondes.map(r => (
              <tr key={r.id}>
                <td>{new Date(r.debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                <td>{r.fin ? new Date(r.fin).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                <td>{r.agent_prenom} {r.agent_nom}</td>
                <td>{r.site_nom}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: r.nb_points_total > 0 ? `${(r.nb_points_scannes / r.nb_points_total) * 100}%` : '0%',
                        height: '100%', background: r.nb_points_scannes >= r.nb_points_total ? '#22C55E' : '#F59E0B',
                        borderRadius: 3,
                      }} />
                    </div>
                    <span style={{ fontSize: 12 }}>{r.nb_points_scannes}/{r.nb_points_total}</span>
                  </div>
                </td>
                <td><span className={`gss-badge ${statutColor(r.statut)}`}>{r.statut}</span></td>
                <td>
                  <button className="gss-btn-icon" onClick={() => voirDetail(r.id)} title="Detail">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── POINTS DE PASSAGE ──────────────────────────────────────────
function TabPoints({ headers }) {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [points, setPoints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', description: '', ordre: 0, obligatoire: true });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
  }, []);

  const loadPoints = useCallback(async () => {
    if (!selectedSite) return setPoints([]);
    try {
      const res = await api.get(`/rondes/sites/${selectedSite}/points`);
      setPoints(res.data);
    } catch {}
  }, [selectedSite]);

  useEffect(() => { loadPoints(); }, [loadPoints]);

  const submit = async () => {
    if (!form.nom) return alert('Nom requis');
    try {
      if (editId) {
        await api.put(`/rondes/points/${editId}`, form);
      } else {
        await api.post(`/rondes/sites/${selectedSite}/points`, form);
      }
      setShowForm(false);
      setEditId(null);
      setForm({ nom: '', description: '', ordre: 0, obligatoire: true });
      loadPoints();
    } catch {}
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ nom: p.nom, description: p.description || '', ordre: p.ordre, obligatoire: p.obligatoire, qr_code: p.qr_code, actif: p.actif });
    setShowForm(true);
  };

  return (
    <div>
      <div className="section-toolbar">
        <select className="gss-input" value={selectedSite} onChange={e => setSelectedSite(e.target.value)} style={{ maxWidth: 300 }}>
          <option value="">-- Selectionner un site --</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
        {selectedSite && (
          <button className="gss-btn primary" onClick={() => { setShowForm(true); setEditId(null); setForm({ nom: '', description: '', ordre: points.length + 1, obligatoire: true }); }}>
            + Ajouter point
          </button>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => { setShowForm(false); setEditId(null); }}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <div>
                  {editId ? 'Modifier' : 'Nouveau'} point de passage
                  <div className="gss-modal-subtitle">Configuration point de controle</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => { setShowForm(false); setEditId(null); }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <span>INFORMATIONS DU POINT</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Nom</label>
                  <input className="gss-input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Ordre</label>
                  <input className="gss-input" type="number" value={form.ordre} onChange={e => setForm(f => ({ ...f, ordre: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                <span>DETAILS & OPTIONS</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Description</label>
                  <input className="gss-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="gss-field" style={{ display: 'flex', alignItems: 'end', gap: 8, paddingBottom: '0.55rem' }}>
                  <input type="checkbox" id="obligatoire" checked={form.obligatoire} onChange={e => setForm(f => ({ ...f, obligatoire: e.target.checked }))} />
                  <label htmlFor="obligatoire" style={{ marginBottom: 0 }}>Obligatoire</label>
                </div>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => { setShowForm(false); setEditId(null); }}>Annuler</button>
              <button className="gss-btn-deploy" onClick={submit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                {editId ? 'Modifier' : 'Creer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedSite ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Selectionnez un site pour voir ses points de passage</div>
      ) : (
        <div className="gss-table-wrapper">
          <table className="gss-table">
            <thead>
              <tr>
                <th>Ordre</th>
                <th>Nom</th>
                <th>Description</th>
                <th>QR Code</th>
                <th>Obligatoire</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {points.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucun point de passage</td></tr>
              )}
              {points.map(p => (
                <tr key={p.id}>
                  <td>{p.ordre}</td>
                  <td>{p.nom}</td>
                  <td>{p.description || '-'}</td>
                  <td><span className="mono" style={{ fontSize: 11 }}>{p.qr_code}</span></td>
                  <td>{p.obligatoire ? <span className="gss-badge warning">OUI</span> : 'Non'}</td>
                  <td>
                    <button className="gss-btn-icon" onClick={() => startEdit(p)} title="Modifier">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── ALERTES ──────────────────────────────────────────
function TabAlertes({ headers }) {
  const [alertes, setAlertes] = useState([]);
  const [filter, setFilter] = useState('false');
  const load = useCallback(async () => {
    try {
      const res = await api.get('/rondes/alertes', { params: { resolu: filter } });
      setAlertes(res.data);
    } catch {}
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const verifier = async () => {
    try {
      const res = await api.post('/rondes/verifier-points');
      alert(`${res.data.alertes_creees} nouvelle(s) alerte(s) creee(s)`);
      load();
    } catch {}
  };

  return (
    <div>
      <div className="section-toolbar">
        <select className="gss-input" value={filter} onChange={e => setFilter(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="false">Non resolues</option>
          <option value="true">Resolues</option>
        </select>
        <button className="gss-btn warning" onClick={verifier}>Verifier les points manques</button>
      </div>

      <div className="gss-table-wrapper">
        <table className="gss-table">
          <thead>
            <tr>
              <th>Date/Heure</th>
              <th>Agent</th>
              <th>Site</th>
              <th>Point manque</th>
              <th>Delai</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {alertes.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucune alerte</td></tr>
            )}
            {alertes.map(a => (
              <tr key={a.id}>
                <td>{new Date(a.heure_alerte).toLocaleString('fr-FR')}</td>
                <td>{a.agent_prenom} {a.agent_nom}</td>
                <td>{a.site_nom}</td>
                <td>{a.point_nom || '-'}</td>
                <td>{a.delai_prevu_min} min</td>
                <td>
                  <span className={`gss-badge ${a.resolu ? 'success' : 'danger'}`}>
                    {a.resolu ? 'Resolu' : 'Active'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
